import React, { useEffect, useState } from 'react'
//components
import { Button } from '../../../../components/ui/buttons/Button'
import Label from '../../../../components/ui/labels/Label'
import { Input } from '../../../../components/ui/inputs/Input'
import { TextInput } from '../../../../components/ui/inputs/TextInput'
import { InputContainer } from '../../../../components/ui/inputs/InputContainers'
import Select from 'react-select'
import { SelectContainer } from '../../../../components/ui/layout/SelectContainer'
import EditableTable from '../../../../components/ui/editableTable/EditableTable'
import { UpdatePoisInGroup } from './UpdatePoisInGroup'
//hooks
import { useLoading } from '../../../../contexts/LoadingContext'
import useUserOptions from '../../../../hooks/useUserOptions'
import useUserOptionsGroups from '../../../../hooks/useUserOptionsGroups'
import { useError } from '../../../../contexts/ErrorContext'
import { useSuccess } from '../../../../contexts/SuccessContext'
import useConfirmDialog from '../../../../hooks/useConfirmDialog'
import { useUser } from '../../../../contexts/UserContext'
import { useUnsavedChanges } from '@contexts/UnsavedChangesContext'
//api
import { 
    updatePOIGroupAPI, 
} from '../../../../services/poiGroupsAPI'
import { 
    addUsersToPOIGroupAPI, 
    removeUsersFromPOIGroupAPI,
    updatePOIGroupUsersAPI
} from '../../../../services/poiGroupUsersAPI'
import { PoiGroupEditorTooltip } from '@components/ui/tooltips/PoiTooltips'

//table schema for POIGroup users in EditableTable component

const POIGroupUserSchema = {
    "label":{
        "title": "Name",
    },
    "IsEditor": {
        "title": (
      <>
        Is POI Group Editor <PoiGroupEditorTooltip />
      </>
    ),
        "inputType": "toggle-state",
        "toggleLocked": false
    }
}

/**
 * 
 * Can add a new poi group, update an existing one, change users, and change pois in group
 *
 * 
 * @param {Object | null} groupDetails - contains Group_Name and Description
 * @param {function} setSelectedGroup - function to set selected group in parent when new group created
 * @param {function} setPermissionSortedPoiGroups - function to set group name options for nested dropdown in parent (from usePoiGroupService)
 * @param {function} setIsAddNewGroup - function to set isAddNewGroup variable
 * @param {Object | null} selectedJob - data about selectedJob if there is one - formatted like: {value: 1, label: Job0910_TestJob} where value is JobID
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - POI
 */

export const PoiGroupDetails = ({
    groupDetails = null,
    setSelectedGroup, 
    setPermissionSortedPoiGroups, 
    selectedJob,
    setStoredPoiGroup
}) => {
    //set state
    const [groupName, setGroupName] = useState("")
    const [groupDescription, setGroupDescription] = useState("")
    
    const [usersAssigned, setUsersAssigned] = useState([])
    const [usersAssignedOriginal, setUsersAssignedOriginal] = useState(null)
    const [isEditor, setIsEditor] = useState()
    const [isInGroup, setIsInGroup] = useState()
    const [userTableSchema, setUserTableSchema] = useState(POIGroupUserSchema)

    const [validationErrors, setValidationErrors] = useState({})

    const {showLoading, hideLoading}  = useLoading()
    const {userNameOptionsJob, fetchUsersForJob} = useUserOptions()
    const {userNameOptionsPoiGroup, fetchUsersForPoiGroup} = useUserOptionsGroups()
    
    const {showError} = useError()
    const {timeoutSuccess} = useSuccess()
    const [ConfirmDialog, requestConfirm] = useConfirmDialog();
    const {user, userGroups} = useUser();
    const {setHasUnsavedChanges} = useUnsavedChanges()

    //if group details change (new group selected in parent) update state
    useEffect(() => {
        if(groupDetails){
            //set state
            setGroupName(groupDetails.POIGroup_Name)
            setGroupDescription(groupDetails.Description)
            
            //get users in group
            fetchUsersForJob(selectedJob.value)
            fetchUsersForPoiGroup(groupDetails.POIGroupID)
            
            return
        }

        //if no selected group, reset state
        setGroupName("")
        setGroupDescription("")
        setUsersAssigned([])
        setUsersAssignedOriginal([])
        setIsInGroup(false)
        setIsEditor(false)
        setUserTableSchema(POIGroupUserSchema)

    }, [groupDetails, selectedJob]); //eslint-disable-line

    useEffect(() => {
        setUsersAssigned(userNameOptionsPoiGroup)
        setUsersAssignedOriginal(JSON.parse(JSON.stringify(userNameOptionsPoiGroup)))

        //try to get currentUser from fetched group users
        const currentUserInGroup = userNameOptionsPoiGroup.find(userOption => userOption.value === user.UserID)

        if(!currentUserInGroup && !userGroups.includes("Admin")){
            setIsInGroup(currentUserInGroup)
            setIsEditor(false)

            setUserTableSchema({
                "label":{
                    "title": "Name",
                },
                "IsEditor": {
                    "title": (
      <>
        Is POI Group Editor <PoiGroupEditorTooltip />
      </>
    ),
                    "inputType": "toggle-state",
                    "toggleLocked": true
                }
            })
        } else if(currentUserInGroup?.IsEditor || userGroups.includes("Admin") || user.UserID === groupDetails.CreatedBy) {
            setIsInGroup(currentUserInGroup)
            setIsEditor(true)

            setUserTableSchema({
                "label":{
                    "title": "Name",
                },
                "IsEditor": {
                    "title": (
      <>
        Is POI Group Editor <PoiGroupEditorTooltip />
      </>
    ),
                    "inputType": "toggle-state",
                    "toggleLocked": false
                }
            })
        } else {
            setIsInGroup(currentUserInGroup)
            setIsEditor(false)

            //since user isn't an editor, update user table schema so toggles are locked
            setUserTableSchema({
                "label":{
                    "title": "Name",
                },
                "IsEditor": {
                    "title": (
      <>
        Is POI Group Editor <PoiGroupEditorTooltip />
      </>
    ),
                    "inputType": "toggle-state",
                    "toggleLocked": true
                }
            })
        }
    }, [userNameOptionsPoiGroup, user, userGroups])
    
    //helper functions

    //update group details
    const handleUpdateGroup = async() => {
        showLoading("Updating POI group...")

        //validation
        const errors = {}

        if(!groupName){
            errors.groupName = "Group name is required"
        }

        if(!groupDescription){
            errors.groupDescription = "Description is required"
        }

        //if there are errors, set state to errors and return
        if(Object.keys(errors).length > 0){
            setValidationErrors(errors)
            hideLoading()
            showError("Could not update group - some form fields are missing")
            return
        }

        setValidationErrors({})

        //call api
        const response = await updatePOIGroupAPI(groupDetails.POIGroupID, {
            POIGroup_Name: groupName,
            Description: groupDescription
        })
                        
        if(!response.success){
            hideLoading()

            showError(`Error updating POI group: ${response.message}`)

            return
        }

        //if name was updated, change the name in the dropdown
        //track it down in the nested options and update
        setPermissionSortedPoiGroups(prevData => prevData.map(group => {
            if (group.options) { 
                return {
                    ...group,
                    options: group.options.map(option =>
                        option.value === groupDetails.POIGroupID && option.label !== groupName
                            ? { ...option, label: groupName }
                            : option
                    )
                };
            }
            return group;
        }));

        //update dropdowns
        setSelectedGroup(prevData => {
            const selectedOption = {
                value: prevData.value,
                label: groupName
            }
            return selectedOption
        })

        setStoredPoiGroup(prevData => {
            const selectedOption = {
                value: prevData.value,
                label: groupName
            }
            return selectedOption
        })


        setHasUnsavedChanges(false)

        hideLoading()

        timeoutSuccess("POI group saved")
    }

    //update users in group
    const handleUpdateGroupUsers = async () => {
        showLoading("Submitting...");
    
        // Get user IDs from current and original groups
        const usersAssignedIds = usersAssigned.map(user => user.value);
        const usersAssignedOriginalIds = usersAssignedOriginal.map(user => user.value);
    
        // Users to add (newly assigned users)
        const usersToAdd = usersAssigned
            .filter(user => !usersAssignedOriginalIds.includes(user.value))
            .map(user => ({ UserID: user.value, IsEditor: user.IsEditor }));
    
        // Users to remove (users no longer assigned)
        const usersToRemove = usersAssignedOriginal
            .filter(user => !usersAssignedIds.includes(user.value))
            .map(user => user.value);
    
        // Users whose IsEditor status has changed
        const usersToUpdate = usersAssigned
            .filter(user => {
                const originalUser = usersAssignedOriginal.find(orig => orig.value === user.value);
                return originalUser && originalUser.IsEditor !== user.IsEditor;
            })
            .map(user => ({ UserID: user.value, IsEditor: user.IsEditor }));
    
        try {
            // Add new users
            if (usersToAdd.length > 0) {
                const response = await addUsersToPOIGroupAPI(groupDetails.POIGroupID, usersToAdd);
                if (!response.success) throw new Error(`Error adding users: ${response.message}`);
            }
    
            // Remove users
            if (usersToRemove.length > 0) {
                const response = await removeUsersFromPOIGroupAPI(groupDetails.POIGroupID, usersToRemove);
                if (!response.success) throw new Error(`Error removing users: ${response.message}`);
            }
    
            // Update users where IsEditor changed
            if (usersToUpdate.length > 0) {
                const response = await updatePOIGroupUsersAPI(groupDetails.POIGroupID, usersToUpdate);
                if (!response.success) throw new Error(`Error updating users: ${response.message}`);
            }
    
            // Update the original state after successful API calls
            // setUsersAssignedOriginal(usersAssigned);
            fetchUsersForPoiGroup(groupDetails.POIGroupID)
            setHasUnsavedChanges(false)
    
            hideLoading();
            timeoutSuccess("Saved");
        } catch (error) {
            hideLoading();
            showError(error.message);
        }
    };


    const handleAddSelfToGroup = async() => {
        showLoading("Adding you to group...")
        //call api
        const response = await addUsersToPOIGroupAPI(groupDetails.POIGroupID, [{UserID: user.UserID, IsEditor: false}])
                    
        if(!response.success){
            hideLoading()

            showError(`Error adding you to group: ${response.message}`)
        
            return
        }

        const newUsersAssigned = [
            ...usersAssigned, 
            {
                label: `${user.First_Name} ${user.Last_Name}`,
                value: user.UserID,
                IsEditor: false
            }
        ]
        //update usersAssigned state
        setUsersAssigned(newUsersAssigned)
        setUsersAssignedOriginal(JSON.parse(JSON.stringify(newUsersAssigned)))
        setIsInGroup(true)

        //move the dropdown option from Not In Group to Viewer
        setPermissionSortedPoiGroups(prevData => {
            let optionToMove = null;
    
            // remove the option from "Not In Group"
            const updatedGroups = prevData.map(group => {
                if (group.label === "Not In Group") {
                    const filteredOptions = group.options.filter(option => {
                        if (option.value === groupDetails.POIGroupID) {
                            optionToMove = option; // store the option for moving
                            return false; // remove it from "Not In Group"
                        }
                        return true;
                    });
                    return { ...group, options: filteredOptions };
                }
                return group;
            });
    
            // add the option to "Viewer"
            return updatedGroups.map(group => {
                if (group.label === "Viewer" && optionToMove) {
                    return { ...group, options: [...group.options, optionToMove] };
                }
                return group;
            });
        });
        
        hideLoading()
        
        //everything submitted successfully, so let user know
        timeoutSuccess("Saved")

    }

    const handleRemoveSelf = async() => {
        showLoading("Removing you from group...")

        //call api
        const isConfirmed = await requestConfirm('Are you sure you want to remove yourself from this group?', 'Yes, Remove Me', 'Cancel');

        if(!isConfirmed){
            hideLoading();
            return
        }

        const response = await removeUsersFromPOIGroupAPI(groupDetails.POIGroupID, [user.UserID])
                    
        if(!response.success){
            hideLoading()

            showError(`Error removing you from group: ${response.message}`)
        
            return
        }

        const newUsersAssigned = usersAssigned.filter(newUser => newUser.value !== user.UserID)

        //update usersAssigned state
        setUsersAssigned(newUsersAssigned)
        setUsersAssignedOriginal(JSON.parse(JSON.stringify(newUsersAssigned)))
        setIsInGroup(false)

        //move dropdown option from Viewer to Not In Group
        setPermissionSortedPoiGroups(prevData => {
            let optionToMove = null;
    
            // remove the option from "Viewer"
            const updatedGroups = prevData.map(group => {
                if (group.label === "Viewer") {
                    const filteredOptions = group.options.filter(option => {
                        if (option.value === groupDetails.POIGroupID) {
                            optionToMove = option; // store the option for moving
                            return false; // remove it from "Viewer"
                        }
                        return true;
                    });
                    return { ...group, options: filteredOptions };
                }
                return group;
            });
    
            // add the option to "Not In Group"
            return updatedGroups.map(group => {
                if (group.label === "Not In Group" && optionToMove) {
                    return { ...group, options: [...group.options, optionToMove] };
                }
                return group;
            });
        });
        
        hideLoading()
        
        //everything submitted successfully, so let user know
        timeoutSuccess("Saved")
    }

    return (

        <div className='flex flex-col'>

            {/* ADD SELF TO GROUP */}
            {!isInGroup ? (
                <div className='my-10 flex flex-col items-center'>
                    <p className='text-md'><i className="fa-solid fa-circle-exclamation mr-2 text-yellow-500"></i> You are not in this group. Would you like to add yourself as a viewer?</p>
                    <Button variant="caution" onClick={() => handleAddSelfToGroup()} className='mt-6'>Add Me To Group</Button>

                </div>
            ) : null}

                <hr className='mb-12 mt-2'/>

            {/* GROUP DETAILS */}

            {/* group name */}
            <InputContainer>
                <Label htmlFor='groupName' className='mb-2 font-semibold'>POI group name</Label>
                <Input 
                    name="groupName" 
                    value={groupName} 
                    onChange={(e)=>setGroupName(e.target.value)} 
                    error={validationErrors.groupName}
                    disabled={!isEditor} 
                />
            </InputContainer>

            {/* group description */}
            <InputContainer>
                <Label htmlFor='groupDescription' className='mb-2 font-semibold'>POI group description</Label>
                <TextInput 
                    name="groupDescription" 
                    value={groupDescription} 
                    onChange={(e)=>setGroupDescription(e.target.value)} 
                    error={validationErrors.groupDescription} 
                    disabled={!isEditor} 
                />
            </InputContainer>

            {isEditor ? (
                <div>
                    <Button variant="success" onClick={() => handleUpdateGroup()}>Update Group Details</Button>
                </div> 
            ) : null}

           
            {/* USERS IN GROUP */}
                        
            <div className="my-10">
                <hr className='mb-10 mt-2'/>

                <Label>Users in group</Label>
                <SelectContainer>
                    <Select
                        value={usersAssigned}
                        options={userNameOptionsJob}
                        name="usersGroupDropdown"
                                    onChange={(x) => setUsersAssigned(x)}
                                    menuPosition='fixed'
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        isMulti={true}
                        isDisabled={!isEditor}
                    />
                </SelectContainer>

                {usersAssigned.length > 0 ? (
                    <EditableTable 
                        data={usersAssigned} 
                        setData={setUsersAssigned} 
                        schema={userTableSchema} 
                        canDeleteRow={false} 
                    />
                ) : null}

                {/* if not an editor, still give them a change to remove themselves from group */}
                {!isEditor && isInGroup ? (
                    <div>
                        <Button variant="danger" onClick={() => handleRemoveSelf()}>Remove Me From Group</Button>
                    </div>
                ) : null}
                            
                {/* only allow editors to update users */}
                {isEditor ? (
                    <div>
                        <Button variant="success" onClick={() => handleUpdateGroupUsers()}>Update Group Users</Button>
                    </div>
                ) 
                : null}
                            
            </div>

            {/* POIS IN GROUP */}

            <UpdatePoisInGroup
                groupDetails={groupDetails}
                isEditor={isEditor}
                selectedJob={selectedJob}
            />

            {/* dialog box to confirm that user wants to remove self from group*/}
            <ConfirmDialog />
        </div>

    )
}